'use client';
import React, { useState, useEffect } from 'react';
import dynamic from 'next/dynamic';
import { Database, Users, Activity, AlertTriangle, CheckCircle, TrendingUp, DollarSign, Gavel, Eye, Download, RefreshCw, MoreVertical, XCircle, Zap, Settings, FileText } from 'lucide-react';
import MetricCard from '@/components/ui/MetricCard';
import StatusBadge from '@/components/ui/StatusBadge';
import SectionHeader from '@/components/ui/SectionHeader';
import { createClient } from '@/lib/supabase/client';
import { downloadCSV, downloadPDF } from '@/lib/auditExport';

const PlatformAUMChart = dynamic(() => import('./PlatformAUMChart'), { ssr: false });
const CampaignPipelineChart = dynamic(() => import('./CampaignPipelineChart'), { ssr: false });

interface PlatformKPIs {
  totalAUM: string;
  activeTenants: number;
  activeCampaigns: number;
  totalInvestors: number;
  pendingApprovals: number;
  systemHealth: number;
  totalTransactions: number;
  escrowTotal: string;
}

interface Campaign {
  id: string;
  campaign_ref: string;
  name: string;
  issuer: string;
  status: string;
  raised_amount: number;
  target_amount: number;
  investor_count: number;
}

interface SettlementBatch {
  id: string;
  batch_ref: string;
  campaign: string;
  amount: number;
  status: string;
  priority: string;
  submitted_by: string;
  created_at: string;
}

interface AuditEntry {
  id: string;
  action_text: string;
  actor_email: string;
  actor_ip: string | null;
  log_level: string;
  created_at: string;
}

interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  role: string;
  kyc_status: string;
  is_active: boolean;
  created_at: string;
}

const systemHealth = [
  { service: 'API Gateway', status: 'operational', latency: '42ms', uptime: '99.99%' },
  { service: 'Auth Service', status: 'operational', latency: '18ms', uptime: '99.98%' },
  { service: 'Escrow Engine', status: 'operational', latency: '87ms', uptime: '100%' },
  { service: 'KYC/AML Service', status: 'degraded', latency: '340ms', uptime: '98.1%' },
  { service: 'Document Vault', status: 'operational', latency: '124ms', uptime: '99.97%' },
  { service: 'Notification Service', status: 'operational', latency: '56ms', uptime: '99.95%' },
  { service: 'Webhook Dispatcher', status: 'operational', latency: '29ms', uptime: '99.92%' },
];

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState<'overview' | 'tenants' | 'approvals' | 'audit' | 'system'>('overview');
  const [kpis, setKpis] = useState<PlatformKPIs>({ totalAUM: '$0', activeTenants: 0, activeCampaigns: 0, totalInvestors: 0, pendingApprovals: 0, systemHealth: 99.97, totalTransactions: 0, escrowTotal: '$0' });
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [pendingBatches, setPendingBatches] = useState<SettlementBatch[]>([]);
  const [auditLog, setAuditLog] = useState<AuditEntry[]>([]);
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  let supabase = createClient();
  const fmt = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

  // ── Export helpers ────────────────────────────────────────────────────────
  const exportAuditCSV = () => {
    const cols = [
      { key: 'created_at', label: 'Timestamp' },
      { key: 'action_text', label: 'Action' },
      { key: 'actor_email', label: 'Actor Email' },
      { key: 'actor_ip', label: 'IP Address' },
      { key: 'log_level', label: 'Log Level' },
    ];
    downloadCSV(auditLog, cols, `platform-audit-log-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const exportAuditPDF = () => {
    const cols = [
      { key: 'created_at', label: 'Timestamp' },
      { key: 'action_text', label: 'Action' },
      { key: 'actor_email', label: 'Actor' },
      { key: 'actor_ip', label: 'IP' },
      { key: 'log_level', label: 'Level' },
    ];
    downloadPDF(auditLog, cols, 'Platform Audit Log — Extract', 'Admin Portal — Regulatory Reporting');
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsRes, campaignsRes, batchesRes, auditRes, usersRes, txnRes] = await Promise.all([
        supabase.from('platform_stats').select('*'),
        supabase.from('campaigns').select('*').order('raised_amount', { ascending: false }).limit(10),
        supabase.from('settlement_batches').select('*').in('status', ['pending_approval', 'on_hold']).order('created_at', { ascending: false }).limit(10),
        supabase.from('platform_audit_log').select('*').order('created_at', { ascending: false }).limit(15),
        supabase.from('user_profiles').select('*').order('created_at', { ascending: false }).limit(20),
        supabase.from('transactions').select('id').limit(1000),
      ]);

      const statsData = statsRes.data || [];
      const campData = campaignsRes.data || [];
      const batchData = batchesRes.data || [];
      const auditData = auditRes.data || [];
      const usersData = usersRes.data || [];
      const txnCount = txnRes.data?.length || 0;

      setCampaigns(campData);
      setPendingBatches(batchData);
      setAuditLog(auditData);
      setUsers(usersData);

      // Build KPIs from stats table or derive from live data
      const getStat = (key: string) => statsData.find(s => s.stat_key === key);
      const totalEscrow = campData.reduce((sum, c) => sum + (c.escrow_balance || 0), 0);
      const totalAUM = campData.reduce((sum, c) => sum + (c.raised_amount || 0), 0);
      const activeCamps = campData.filter(c => c.status === 'published').length;

      setKpis({
        totalAUM: getStat('total_aum')?.stat_value || fmt(totalAUM),
        activeTenants: parseInt(getStat('active_tenants')?.stat_value || '14'),
        activeCampaigns: activeCamps || parseInt(getStat('active_campaigns')?.stat_value || '0'),
        totalInvestors: usersData.filter(u => u.role === 'investor').length || parseInt(getStat('total_investors')?.stat_value || '0'),
        pendingApprovals: batchData.length || parseInt(getStat('pending_approvals')?.stat_value || '0'),
        systemHealth: parseFloat(getStat('system_health_pct')?.stat_value || '99.97'),
        totalTransactions: txnCount || parseInt(getStat('total_transactions')?.stat_value || '0'),
        escrowTotal: getStat('escrow_total')?.stat_value || fmt(totalEscrow),
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // ── Real-time data subscriptions ──────────────────────────────────────────
  useEffect(() => {
    let supabase: any = null;
    const subs: any[] = [];
    try {
      supabase = createClient();
      const handler = () => { fetchData(); };

      subs.push(
        supabase
          .channel('admin-rt-campaigns')
          .on('postgres_changes', { event: '*', schema: 'public', table: 'campaigns' }, handler)
          .subscribe(),
        supabase
          .channel('admin-rt-batches')
          .on('postgres_changes', { event: '*', schema: 'public', table: 'settlement_batches' }, handler)
          .subscribe(),
        supabase
          .channel('admin-rt-audit')
          .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'platform_audit_log' }, handler)
          .subscribe(),
        supabase
          .channel('admin-rt-users')
          .on('postgres_changes', { event: '*', schema: 'public', table: 'user_profiles' }, handler)
          .subscribe(),
        supabase
          .channel('admin-rt-stats')
          .on('postgres_changes', { event: '*', schema: 'public', table: 'platform_stats' }, handler)
          .subscribe(),
      );
    } catch {
      // Supabase not configured — subscriptions skipped
    }
    return () => {
      if (supabase) subs.forEach((ch) => supabase.removeChannel(ch));
    };
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-4 flex-wrap">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground tracking-tight">Platform Administration</h1>
          <p className="text-sm text-muted-foreground mt-1">CrowdVault™ Super Admin · All systems monitored · Live data</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-positive/10 border border-positive/20 text-xs font-semibold text-positive">
            <span className="pulse-dot" />
            Platform Operational
          </div>
          <button onClick={fetchData} className="btn-secondary text-xs gap-1.5" disabled={loading}><RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh</button>
        </div>
      </div>

      {error && (
        <div className="alert-banner alert-warning">
          <AlertTriangle size={16} className="flex-shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Critical alerts */}
      {kpis.pendingApprovals > 0 && (
        <div className="alert-banner alert-critical">
          <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-sm">{kpis.pendingApprovals} Approval{kpis.pendingApprovals > 1 ? 's' : ''} Require Action</p>
            <p className="text-xs text-muted-foreground mt-0.5">Settlement batches pending four-eyes approval. Maker-checker enforcement active.</p>
          </div>
          <button className="ml-auto flex-shrink-0 btn-ghost text-xs gap-1" onClick={() => setActiveTab('approvals')}>
            Review Now <AlertTriangle size={11} />
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-0 border-b border-border overflow-x-auto scrollbar-hide">
        {(['overview', 'tenants', 'approvals', 'audit', 'system'] as const).map((tab) => (
          <button key={`admin-tab-${tab}`} onClick={() => setActiveTab(tab)} className={`portal-tab capitalize flex-shrink-0 ${activeTab === tab ? 'active' : ''}`}>
            {tab}
            {tab === 'approvals' && kpis.pendingApprovals > 0 && (
              <span className="ml-1.5 text-xs bg-warning/20 text-warning rounded-full px-1.5 py-0.5 font-mono-data">{kpis.pendingApprovals}</span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <RefreshCw size={24} className="animate-spin text-muted-foreground" />
          <span className="ml-3 text-muted-foreground text-sm">Loading platform data...</span>
        </div>
      ) : (
        <>
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="glass-card rounded-xl p-4 card-hover lg:col-span-2">
                  <div className="flex items-start justify-between mb-2">
                    <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Platform AUM</p>
                    <div className="flex items-center gap-1.5">
                      <span className="pulse-dot" />
                      <span className="text-xs text-muted-foreground">Live</span>
                    </div>
                  </div>
                  <p className="text-4xl font-bold font-mono-data text-foreground mb-1">{kpis.totalAUM}</p>
                  <p className="text-xs text-muted-foreground mb-3">Across {kpis.activeTenants} active tenants · {kpis.activeCampaigns} live campaigns</p>
                  <PlatformAUMChart />
                </div>
                <MetricCard label="Active Tenants" value={String(kpis.activeTenants)} subValue="Multi-tenant deployments" trend={7.1} trendLabel="vs last quarter" variant="info" icon={Database} iconColor="text-info" />
                <MetricCard label="Pending Approvals" value={String(kpis.pendingApprovals)} subValue="Settlement batches awaiting sign-off" variant={kpis.pendingApprovals > 0 ? 'critical' : 'positive'} icon={Gavel} iconColor={kpis.pendingApprovals > 0 ? 'text-critical' : 'text-positive'} />
                <MetricCard label="Total Investors" value={kpis.totalInvestors.toLocaleString()} subValue="Registered investor accounts" trend={14.3} trendLabel="MoM" variant="default" icon={Users} iconColor="text-primary" />
                <MetricCard label="Escrow Held" value={kpis.escrowTotal} subValue="Regulated escrow positions" variant="positive" icon={DollarSign} iconColor="text-positive" mono />
                <MetricCard label="Platform Uptime" value={`${kpis.systemHealth}%`} subValue="30-day rolling average" variant="positive" icon={Activity} iconColor="text-positive" mono />
                <MetricCard label="Total Transactions" value={kpis.totalTransactions.toLocaleString()} subValue="All-time transactions" variant="default" icon={TrendingUp} iconColor="text-accent" />
              </div>
              <div className="glass-card rounded-xl p-4">
                <SectionHeader title="Campaign Pipeline by Stage" subtitle={`${campaigns.length} campaigns across all tenants`} />
                <CampaignPipelineChart />
              </div>
            </div>
          )}

          {activeTab === 'tenants' && (
            <div className="glass-card rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                <SectionHeader title="Active Campaigns" subtitle={`${campaigns.length} campaigns`} />
                <button className="btn-primary text-xs gap-1.5"><Zap size={13} /> New Campaign</button>
              </div>
              {campaigns.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground text-sm">No campaigns found.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-surface-3">
                      <tr>
                        {['Campaign', 'Issuer', 'Raised', 'Target', 'Investors', 'Status', 'Actions'].map((h) => (
                          <th key={`th-camp-${h}`} className="table-th">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {campaigns.map((camp) => {
                        const pct = camp.target_amount > 0 ? Math.round((camp.raised_amount / camp.target_amount) * 100) : 0;
                        return (
                          <tr key={camp.id} className="data-table-row">
                            <td className="table-td">
                              <p className="font-medium text-foreground">{camp.name}</p>
                              <p className="text-xs font-mono-data text-muted-foreground">{camp.campaign_ref}</p>
                            </td>
                            <td className="table-td text-xs text-muted-foreground">{camp.issuer}</td>
                            <td className="table-td font-mono-data font-semibold text-foreground">{fmt(camp.raised_amount)}</td>
                            <td className="table-td">
                              <div>
                                <span className="font-mono-data text-xs text-muted-foreground">{fmt(camp.target_amount)}</span>
                                <div className="funding-thermometer mt-1 w-20">
                                  <div className="funding-thermometer-fill" style={{ width: `${pct}%` }} />
                                </div>
                              </div>
                            </td>
                            <td className="table-td font-mono-data text-sm text-foreground">{camp.investor_count}</td>
                            <td className="table-td">
                              <StatusBadge variant={camp.status === 'published' ? 'published' : camp.status === 'closed' ? 'settled' : 'pending'} label={camp.status.charAt(0).toUpperCase() + camp.status.slice(1)} dot />
                            </td>
                            <td className="table-td">
                              <div className="flex items-center gap-1">
                                <button className="btn-ghost p-1" title="View"><Eye size={13} /></button>
                                <button className="btn-ghost p-1" title="Settings"><Settings size={13} /></button>
                                <button className="btn-ghost p-1" title="More"><MoreVertical size={13} /></button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'approvals' && (
            <div className="glass-card rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                <SectionHeader title="Maker-Checker Approval Queue" subtitle="Four-eyes enforcement · Settlement batches requiring dual authorisation" />
                <span className="status-badge badge-critical">{pendingBatches.length} Pending</span>
              </div>
              {pendingBatches.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground text-sm">No pending approvals. All clear.</div>
              ) : (
                <div className="divide-y divide-border">
                  {pendingBatches.map((batch) => (
                    <div key={batch.id} className={`px-4 py-3.5 hover:bg-surface-3/50 transition-colors ${batch.priority === 'critical' ? 'border-l-2 border-l-critical' : batch.priority === 'high' ? 'border-l-2 border-l-warning' : ''}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <p className="font-semibold text-sm text-foreground">Settlement Batch: {batch.batch_ref}</p>
                            <StatusBadge variant={batch.priority === 'critical' ? 'critical' : batch.priority === 'high' ? 'warning' : 'review'} label={batch.priority.toUpperCase()} />
                          </div>
                          <p className="text-xs text-muted-foreground mb-0.5">Campaign: <span className="text-secondary-foreground">{batch.campaign}</span></p>
                          <p className="text-xs text-muted-foreground">Amount: <span className="font-mono-data font-bold text-foreground">{fmt(batch.amount)}</span> · {batch.investor_count} investors · Submitted by: {batch.submitted_by}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-positive/15 text-positive hover:bg-positive/25 transition-colors border border-positive/20">
                            <CheckCircle size={13} /> Approve
                          </button>
                          <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-negative/15 text-negative hover:bg-negative/25 transition-colors border border-negative/20">
                            <XCircle size={13} /> Reject
                          </button>
                          <button className="btn-ghost p-1.5" title="View details"><Eye size={13} /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'audit' && (
            <div className="glass-card rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                <SectionHeader title="Platform Audit Log" subtitle={`${auditLog.length} recent entries · Append-only`} />
                <div className="flex items-center gap-2">
                  <button onClick={exportAuditCSV} className="btn-ghost text-xs gap-1.5" title="Download CSV"><Download size={13} /> CSV</button>
                  <button onClick={exportAuditPDF} className="btn-ghost text-xs gap-1.5" title="Download PDF"><FileText size={13} /> PDF</button>
                </div>
              </div>
              {auditLog.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground text-sm">No audit log entries found.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-surface-3">
                      <tr>
                        {['Time', 'Action', 'User', 'IP Address', 'Level'].map((h) => (
                          <th key={`th-audit-${h}`} className="table-th">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {auditLog.map((entry) => (
                        <tr key={entry.id} className="data-table-row">
                          <td className="table-td font-mono-data text-xs text-muted-foreground whitespace-nowrap">{new Date(entry.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</td>
                          <td className="table-td text-xs text-foreground max-w-xs">{entry.action_text}</td>
                          <td className="table-td font-mono-data text-xs text-muted-foreground">{entry.actor_email}</td>
                          <td className="table-td font-mono-data text-xs text-muted-foreground">{entry.actor_ip || 'internal'}</td>
                          <td className="table-td">
                            <StatusBadge
                              variant={entry.log_level === 'critical' ? 'critical' : entry.log_level === 'warning' ? 'warning' : 'confirmed'}
                              label={entry.log_level.toUpperCase()}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'system' && (
            <div className="space-y-4">
              <SectionHeader title="System Health Monitor" subtitle="Service status and performance metrics" />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {systemHealth.map((svc) => (
                  <div key={svc.service} className={`glass-card rounded-xl p-4 ${svc.status === 'degraded' ? 'metric-warning' : ''}`}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-semibold text-sm text-foreground">{svc.service}</p>
                      <StatusBadge variant={svc.status === 'operational' ? 'confirmed' : 'warning'} label={svc.status === 'operational' ? 'Operational' : 'Degraded'} dot />
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span>Latency: <span className="font-mono-data text-foreground">{svc.latency}</span></span>
                      <span>Uptime: <span className="font-mono-data text-foreground">{svc.uptime}</span></span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}